/**
 * tableHeaders.ts — Re-export barrel for all table column header definitions.
 *
 * Source of truth has moved to `~/utils/table-headers/`.
 * This file is kept as a stable import point so existing consumers don't need
 * to update their import paths during the migration.
 */

// Header icon registry + shared dialog header type
export { getHeaderIcon, type InstrumentDialogHeader } from "~/utils/table-headers/header-icons";

// Locations domain
export { getLocationsHeaders } from "~/utils/table-headers/locations-list";

// Instruments domain
export { getInstrumentsHeaders } from "~/utils/table-headers/instruments-list";

// Channels domain
export { getChannelsHeaders } from "~/utils/table-headers/channels-list";

export { getSampleTypesHeaders } from "~/utils/table-headers/instruments-sample-types";
export { getRightsHeaders } from "~/utils/table-headers/instruments-rights";
export { getFlagsDialogHeaders } from "~/utils/table-headers/instruments-flags";
export { getPanelsHeaders } from "~/utils/table-headers/instruments-panels";
export { getPanelMappingsHeaders } from "~/utils/table-headers/panel-mappings";
export { getPanelTestsHeaders } from "~/utils/table-headers/panel-tests";

// Tests domain
export { getTestsHeaders } from "~/utils/table-headers/tests-list";
export { getMappingsHeaders } from "~/utils/table-headers/tests-mappings";
export { getProcessorsHeaders } from "~/utils/table-headers/tests-processors";

/**
 *  Results Headers
 */

export const getResultsHeaders = (t: (key: string) => string, smAndDown: boolean) =>
  [
    {
      title: t("components.common.tables.headers.results"),
      key: "data-table-group",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.test"),
      key: "lisName",
      sortable: false,
      maxWidth: "160px",
    },
    {
      title: t("components.common.tables.headers.flags"),
      key: "flags",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("center" as const),
    },
    {
      title: t("components.common.tables.headers.result"),
      key: "displayResult",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("end" as const),
    },
    {
      title: t("components.common.tables.headers.actions"),
      key: "actions",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("center" as const),
    },
    {
      title: t("components.common.tables.headers.current"),
      key: "currentValue",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("end" as const),
    },
    {
      title: t("components.common.tables.headers.units"),
      key: "units",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.refRange"),
      key: "rangeText",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.resultTime"),
      key: "resultTime",
      sortable: false,
    },
  ] as const;

/**
 * History Dialog
 */

export const getHistoryResultsHeaders = (t: (key: string) => string, smAndDown: boolean) =>
  [
    {
      title: t("components.common.tables.headers.resultTime"),
      key: "resultTime",
      sortable: true,
    },
    {
      title: t("components.common.tables.headers.flags"),
      key: "flags",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("center" as const),
    },
    {
      title: t("components.common.tables.headers.result"),
      key: "result",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("end" as const),
    },
    {
      title: t("components.common.tables.headers.units"),
      key: "units",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.refRange"),
      key: "rangeText",
      sortable: false,
    },
  ] as const;

/**
 * Sample Info Dialog
 */

export const getSampleInfoBasicHeaders = (t: (key: string) => string, smAndDown: boolean) =>
  [
    {
      title: t("components.common.tables.headers.visit_id"),
      key: "visitId",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.barcode"),
      key: "barcode",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.sample_type"),
      key: "sampleType",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.resultTime"),
      key: "taken",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.total_results"),
      key: "totalResults",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("center" as const),
    },
  ] as const;

export const getSampleInfoInstrumentsHeaders = (t: (key: string) => string, smAndDown: boolean) =>
  [
    {
      title: t("components.common.tables.headers.instrument"),
      key: "instrumentName",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.disk_rack"),
      key: "diskOrRack",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("center" as const),
    },
    {
      title: t("components.common.tables.headers.position"),
      key: "position",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("center" as const),
    },
    {
      title: t("components.common.tables.headers.sample_number"),
      key: "sampleNumber",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("center" as const),
    },
    {
      title: t("components.common.tables.headers.sample_type"),
      key: "sampleType",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.sets_count"),
      key: "setsCount",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("center" as const),
    },
    {
      title: t("components.common.tables.headers.pending_results"),
      key: "pendingCount",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("center" as const),
    },
    {
      title: t("components.common.tables.headers.approved_results"),
      key: "approvedCount",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("center" as const),
    },
  ] as const;

export const getSampleInfoResultsSetsHeaders = (t: (key: string) => string, smAndDown: boolean) =>
  [
    {
      title: t("components.common.tables.headers.test"),
      key: "testName",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.flags"),
      key: "flags",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("center" as const),
    },
    {
      title: t("components.common.tables.headers.result"),
      key: "result",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("end" as const),
    },
    {
      title: t("components.common.tables.headers.units"),
      key: "units",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.refRange"),
      key: "rangeText",
      sortable: false,
    },
    {
      title: t("components.common.tables.headers.resultTime"),
      key: "resultTime",
      sortable: false,
      align: smAndDown ? ("start" as const) : ("end" as const),
    },
  ] as const;

/**
 * Flags Dialog
 */
export const getFlagsHeaders = (t: (key: string) => string, smAndDown: boolean) =>
  [
    {
      title: t("pages.validation.dialogs.flags.headers.level"),
      key: "level",
      sortable: false,
    },
    {
      title: t("pages.validation.dialogs.flags.headers.source"),
      key: "source",
      sortable: false,
    },
    {
      title: t("pages.validation.dialogs.flags.headers.code"),
      key: "code",
      sortable: false,
    },
    {
      title: t("pages.validation.dialogs.flags.headers.description"),
      key: "description",
      sortable: false,
    },
  ] as const;
